import { Modul_Event_Map } from "@aleksundshantu/schirdewan-standard-types/acf-module";
import { EventMapOptionsType } from "@/types/ModuleOptionsType";
import { Section } from "@/components/Section/Section";
import { useSublinesVisibility } from "@/hooks/useSublinesOverlinesVisibility";
import { Content } from "@/components/Content/Content";
import { columnSize } from "@/utils/layout/columnSize";
import { ExtendedConsentManagement } from "@/components/ExtendedConsentManagement/ExtendedConsentManagement";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import GoogleMapByLatLng from "../GoogleMap/GoogleMap";
import ModalContent from "@/components/_primitives/Modal/ModalContent/ModalContent";
import ModalRoot from "@/components/_primitives/Modal/ModalRoot/ModalRoot";
import ModalPortal from "@/components/_primitives/Modal/ModalPortal/ModalPortal";
import ModalOverlay from "@/components/_primitives/Modal/ModalOverlay/ModalOverlay";
import { EventCard, EventCardType } from "@/components/EventCard/EventCard";
import { ModalClose } from "@/components/_primitives/Modal/ModalClose/ModalClose";
import styles from "./EventMap.module.scss";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG";
import { fetchEvents } from "@/getData/fetchEvents";
import { addYears } from "date-fns";

interface GoogleMapModuleProps {
  data: Modul_Event_Map;
  eventMapOptions: EventMapOptionsType;
}

const ITEMS_PER_PAGE = 2;

const EventMapModule = ({ data, eventMapOptions }: GoogleMapModuleProps) => {
  const {
    background_color_class,
    id,
    headline_text_align_class,
    module_width,
    section_margin_class,
    section_padding_class,
    text_width,
  } = data.layout;

  const { headline, headline_html_tag, content, overline, subline } =
    data.event_map;
  const { getIcon, getLabel } = useGlobalContext();
  const { locale, defaultLocale } = useRouter();

  const [events, setEvents] = useState<EventCardType[]>([]);

  useEffect(() => {
    fetchEvents({
      defaultLocale,
      locale,
      startDate: new Date(),
      endDate: addYears(new Date(), 10),
    }).then(({ posts }) => {
      setEvents(posts.filter((p) => p.location?.address_googlemaps));
    });
  }, [defaultLocale, locale]);

  const { show_overline_subline, columns } = eventMapOptions;
  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline,
  );

  const [selectedEvents, setSelectedEvents] = useState<number[]>([]);
  const [modalPage, setModalPage] = useState<number>(1);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const modalContentEventsRef = useRef<HTMLDivElement | null>(null);

  const start = (modalPage - 1) * ITEMS_PER_PAGE;
  const end = start + ITEMS_PER_PAGE;
  const pages = Math.ceil(selectedEvents.length / ITEMS_PER_PAGE);

  const canNextPage = modalPage < pages;
  const canPrevPage = modalPage > 1;
  const nextPage = () => {
    if (modalPage < pages) {
      setModalPage(modalPage + 1);
      modalContentEventsRef.current?.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    }
  };
  const prevPage = () => {
    if (canPrevPage) {
      setModalPage(modalPage - 1);
      modalContentEventsRef.current?.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    }
  };

  const Pagination = () => {
    return (
      <div className={styles["modal-content-pagination"]}>
        <button
          disabled={!canPrevPage}
          onClick={prevPage}
          className={`button-reset ${styles["pagination-arrow"]}`}
        >
          <CustomSVG
            width={16}
            height={16}
            src={getIcon("arrowLeft")}
            className={`is-flex`}
            wrapper={"span"}
            aria-hidden
            data-disabled={!canPrevPage}
          />
        </button>

        <button
          disabled={!canNextPage}
          onClick={nextPage}
          className={`button-reset ${styles["pagination-arrow"]}`}
        >
          <CustomSVG
            width={16}
            height={16}
            src={getIcon("arrowRight")}
            wrapper={"span"}
            className={`is-flex`}
            aria-hidden
          />
        </button>
      </div>
    );
  };

  const memoizedMap = useMemo(() => {
    return (
      <GoogleMapByLatLng
        clusterer={{
          clusterLabel: getLabel("aria_label_event_map_cluster"),
          onClusterClick(markers) {
            setSelectedEvents(markers.map((m) => m.eventId));
          },
        }}
        onMarkerClick={(marker) => {
          setSelectedEvents([marker.eventId]);
        }}
        alignment={{
          autoCenterAndZoom: true,
          fallbackZoom: 5,
          fallbackCenter: { lat: 51.163, lng: 10.447 },
        }}
        markers={events.map((event) => {
          if (!event.location) throw new Error("Event location is missing");
          return {
            lat: event.location.address_googlemaps.lat,
            lng: event.location.address_googlemaps.lng,
            title: event.title ?? "",
            eventId: event.id,
          };
        })}
      />
    );
  }, [events, getLabel]);

  const eventMapJSX = (
    <>
      <Content
        headline={headline}
        headline_html_tag={headline_html_tag}
        headline_text_align_class={headline_text_align_class}
        text_align_class={headline_text_align_class}
        subline={showSubline ? subline : ""}
        overline={showOverline ? overline : ""}
        text={content}
        link={false}
        frameless={module_width === "frameless"}
        button_type="primary"
        contentExtraClass={
          text_width === "narrow-frame"
            ? `column ${columnSize(columns) ?? "is-12-tablet is-10-desktop is-8-fullhd"} ${headline_text_align_class === "has-text-centered" ? "mx-auto" : ""}`
            : ""
        }
      />
      <ExtendedConsentManagement
        type_consent_management="Google Maps"
        className={styles["map-blocker"]}
      >
        <div className={styles["event-map-container"]} ref={modalRef}>
          {memoizedMap}
          <ModalRoot
            open={selectedEvents.length > 0}
            setOpen={(open) => {
              if (!open) {
                setSelectedEvents([]);
                setModalPage(1);
              }
            }}
            absolutePosition
          >
            <ModalPortal portalRef={modalRef.current}>
              <ModalOverlay />
              <ModalContent>
                <ModalClose />
                <div className={styles["modal-content"]}>
                  <div
                    className={styles["modal-content-events"]}
                    data-lenis-prevent
                    ref={modalContentEventsRef}
                  >
                    {events
                      .filter((e) => selectedEvents.includes(e.id))
                      .slice(start, end)
                      .map((event, key) => {
                        return (
                          <EventCard
                            key={key}
                            element={event}
                            headlineTag="h2"
                            moduleType=""
                            showBox={false}
                            show_border_radius={false}
                            show_box_shadow={false}
                            showExcerpt={false}
                            datePosition="left-of-time"
                          />
                        );
                      })}
                  </div>
                  {pages > 1 && <Pagination />}
                </div>
              </ModalContent>
            </ModalPortal>
          </ModalRoot>
        </div>
      </ExtendedConsentManagement>
    </>
  );

  const noColumns =
    module_width === "normal-frame" || module_width === "frameless";

  return (
    <Section
      id={id}
      className={`${styles["event-map"]} ${background_color_class} ${section_padding_class} ${section_margin_class}`}
      frameless={module_width === "frameless"}
      disableColumns
      containerClassName={noColumns ? styles["event-map-content"] : ""}
    >
      {module_width === "normal-frame" || module_width === "frameless" ? (
        eventMapJSX
      ) : (
        <div className="columns is-centered ">
          <div
            className={`column ${columnSize(columns)} is-centered ${styles["event-map-content"]}`}
          >
            {eventMapJSX}
          </div>
        </div>
      )}
    </Section>
  );
};

export default EventMapModule;
